import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Form } from 'react-bootstrap';
import {
  AdvertiserType,
  AuditResult,
  SECTION_LABELS
} from '@blisspointmedia/bpm-types/dist/GoogleAdsAudit';
import { AgentSessionManager } from '@Components/AgentChat';
import { Button } from '@Components/Button';
import { Design, Size } from '@Components/designConstants';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { Agent, useAgentEndpoint } from '../../../utils/agent-endpoints';
import {
  createAuditRun,
  getAdvertiserType,
  getAuditRun,
  listAuditRuns,
  setAdvertiserType
} from '../google-ads-audit-api';
import { SECTION_ORDER } from '../google-ads-audit-constants';
import { summarizeAudit } from '../engine/health-score';
import { AuditProgress } from './AuditProgress';
import { AuditResults } from './AuditResults';
import { BrandConfigDrawer } from './BrandConfigDrawer';
import { PrintView } from './PrintView';

const ADVERTISER_TYPES: AdvertiserType[] = ['universal', 'ecommerce', 'leadgen', 'local', 'app'];
const isAdvertiserType = (value: string): value is AdvertiserType =>
  ADVERTISER_TYPES.includes(value as AdvertiserType);

// Summarizes the current run's findings for the "Ask AI" chat's grounding context —
// mirrors bliss-audit's system-prompt injection of medium/large findings, folded into
// AgentSessionManager's sessionContext since it has no system-message channel.
const buildFindingsSummary = (results: AuditResult[]): string => {
  const notable = results.filter(r => r.rating === 'medium' || r.rating === 'large');
  if (notable.length === 0) return '';
  return [
    'Current Google Ads audit findings (medium/large only):',
    ...notable.map(
      r => `- [${r.rating.toUpperCase()}] ${r.label}${r.comments ? `: ${r.comments}` : ''}`
    )
  ].join('\n');
};

interface WorkspaceHeaderProps {
  accountName: string;
  running: boolean;
  hasRun: boolean;
  chatOpen: boolean;
  onBack: () => void;
  onRunAudit: () => void;
  onToggleChat: () => void;
  onOpenConfig: () => void;
  onOpenPrint: () => void;
}

const WorkspaceHeader = ({
  accountName,
  running,
  hasRun,
  chatOpen,
  onBack,
  onRunAudit,
  onToggleChat,
  onOpenConfig,
  onOpenPrint
}: WorkspaceHeaderProps) => (
  <div className='google-ads-audit-workspace-header'>
    <InteractiveDiv onClick={onBack} className='google-ads-audit-back-link'>
      ← Accounts
    </InteractiveDiv>
    <span className='google-ads-audit-workspace-title'>{accountName}</span>
    {hasRun && (
      <Button design={Design.NEUTRAL} size={Size.SM} onClick={onOpenPrint}>
        Download PDF
      </Button>
    )}
    <Button design={Design.NEUTRAL} size={Size.SM} onClick={onOpenConfig}>
      Configure
    </Button>
    <Button design={chatOpen ? Design.BRAND : Design.NEUTRAL} size={Size.SM} onClick={onToggleChat}>
      Ask AI
    </Button>
    <Button size={Size.SM} onClick={onRunAudit} loading={running}>
      {hasRun ? 'Re-run Audit' : 'Run Audit'}
    </Button>
  </div>
);

interface WorkspaceSidebarProps {
  counts: { large: number; medium: number; small: number; ok: number };
  bySection: Record<string, number>;
  activeSection: string;
  onSectionChange: (section: string) => void;
}

const WorkspaceSidebar = ({
  counts,
  bySection,
  activeSection,
  onSectionChange
}: WorkspaceSidebarProps) => (
  <div className='google-ads-audit-workspace-sidebar'>
    <div className='google-ads-audit-severity-tiles'>
      {counts.large > 0 && <span className='rating-large'>{counts.large} Large</span>}
      {counts.medium > 0 && <span className='rating-medium'>{counts.medium} Medium</span>}
      {counts.small > 0 && <span className='rating-small'>{counts.small} Small</span>}
      {counts.ok > 0 && <span className='rating-ok'>{counts.ok} OK</span>}
    </div>
    <nav className='google-ads-audit-section-nav'>
      <InteractiveDiv
        className={activeSection === 'all' ? 'active' : ''}
        onClick={() => onSectionChange('all')}
      >
        All Checks
      </InteractiveDiv>
      {SECTION_ORDER.filter(s => bySection[s]).map(sectionId => (
        <InteractiveDiv
          key={sectionId}
          className={activeSection === sectionId ? 'active' : ''}
          onClick={() => onSectionChange(sectionId)}
        >
          {SECTION_LABELS[sectionId]}
          <span className='google-ads-audit-section-nav-count'>{bySection[sectionId]}</span>
        </InteractiveDiv>
      ))}
    </nav>
  </div>
);

interface AccountWorkspaceProps {
  company: string;
  accountId: string;
  accountName: string;
  onBack: () => void;
}

export const AccountWorkspace = ({
  company,
  accountId,
  accountName,
  onBack
}: AccountWorkspaceProps) => {
  const queryClient = useQueryClient();
  const [activeSection, setActiveSection] = useState('all');
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [runStartMs, setRunStartMs] = useState<number | null>(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const agentEndpoint = useAgentEndpoint(Agent.AUDIT);

  const { data: advertiserType } = useQuery({
    queryKey: ['google-ads-audit-advertiser-type', company, accountId],
    queryFn: () => getAdvertiserType(company, accountId)
  });

  const { data: runs } = useQuery({
    queryKey: ['google-ads-audit-runs', company],
    queryFn: () => listAuditRuns(company)
  });

  // `runs` is sorted newest-first by the backend. Skip past a newer FAILED run so an
  // older successful audit doesn't disappear from view once a later re-run fails.
  const accountRuns = runs?.filter(r => r.accountId === accountId) ?? [];
  const latestRunId =
    activeRunId ??
    accountRuns.find(r => r.status !== 'FAILED')?.id ??
    accountRuns.at(0)?.id ??
    null;

  const { data: runDetail } = useQuery({
    queryKey: ['google-ads-audit-run', latestRunId],
    queryFn: () => getAuditRun(company, latestRunId as string),
    enabled: Boolean(latestRunId),
    refetchInterval: query => (query.state.data?.run.status === 'RUNNING' ? 2000 : false),
    // The agent call behind a run can take many minutes — without this, polling silently
    // pauses the moment the tab loses focus, and it looks (falsely) like the run stalled.
    refetchIntervalInBackground: true
  });

  const runAuditMutation = useMutation({
    mutationFn: () => createAuditRun(company, { customerId: accountId, accountName }),
    onMutate: () => setRunStartMs(Date.now()),
    onSuccess: response => {
      setActiveRunId(response.runId);
      queryClient.invalidateQueries({ queryKey: ['google-ads-audit-runs', company] });
    }
  });

  const updateAdvertiserTypeMutation = useMutation({
    mutationFn: (type: AdvertiserType) => setAdvertiserType(company, accountId, type, accountName),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['google-ads-audit-advertiser-type', company, accountId]
      })
  });

  const running = runDetail?.run.status === 'RUNNING' || runAuditMutation.isPending;
  const results = runDetail?.results ?? [];
  const summary = summarizeAudit(results);
  const bySection = results.reduce<Record<string, number>>((acc, r) => {
    acc[r.sectionId] = (acc[r.sectionId] ?? 0) + 1;
    return acc;
  }, {});

  if (printOpen && runDetail) {
    return (
      <PrintView
        accountName={accountName}
        runDate={runDetail.run.completedAt ?? runDetail.run.startedAt}
        results={results}
        onClose={() => setPrintOpen(false)}
      />
    );
  }

  return (
    <div className='google-ads-audit-workspace'>
      <WorkspaceHeader
        accountName={accountName}
        running={running}
        hasRun={runDetail?.run.status === 'COMPLETE'}
        chatOpen={chatOpen}
        onBack={onBack}
        onRunAudit={() => runAuditMutation.mutate()}
        onToggleChat={() => setChatOpen(v => !v)}
        onOpenConfig={() => setConfigOpen(true)}
        onOpenPrint={() => setPrintOpen(true)}
      />
      {advertiserType && (
        <Form.Select
          className='google-ads-audit-advertiser-type-select'
          value={advertiserType.advertiserType}
          onChange={e => {
            if (isAdvertiserType(e.target.value)) {
              updateAdvertiserTypeMutation.mutate(e.target.value);
            }
          }}
        >
          <option value='universal'>Universal</option>
          <option value='ecommerce'>E-commerce</option>
          <option value='leadgen'>Lead Gen</option>
          <option value='local'>Local</option>
          <option value='app'>App</option>
        </Form.Select>
      )}
      <div className='google-ads-audit-workspace-body'>
        {running && (
          <AuditProgress
            accountName={accountName}
            totalSections={SECTION_ORDER.length}
            startMs={runStartMs}
            currentActivity={runDetail?.run.currentActivity}
          />
        )}
        {!running && !runDetail && (
          <div className='google-ads-audit-empty-state'>
            No audit yet. Click &quot;Run Audit&quot; to get started.
          </div>
        )}
        {!running && runDetail?.run.status === 'FAILED' && (
          <div className='google-ads-audit-empty-state'>
            Audit failed: {runDetail.run.parseError ?? 'Unknown error.'}
          </div>
        )}
        {!running && runDetail && runDetail.run.status !== 'FAILED' && (
          <>
            <WorkspaceSidebar
              counts={summary.counts}
              bySection={bySection}
              activeSection={activeSection}
              onSectionChange={setActiveSection}
            />
            <AuditResults results={results} activeSection={activeSection} />
          </>
        )}
        {chatOpen && (
          <AgentSessionManager
            agentEndpoint={agentEndpoint}
            agent={Agent.AUDIT}
            company={company}
            className='google-ads-audit-chat-panel'
            sessionContext={buildFindingsSummary(results)}
            focusContext={`Auditing Google Ads account ${accountId} (${accountName}).`}
          />
        )}
      </div>
      <BrandConfigDrawer
        company={company}
        accountId={accountId}
        accountName={accountName}
        show={configOpen}
        onClose={() => setConfigOpen(false)}
      />
    </div>
  );
};
